<template>
  <div class="pa-0">
    <!-- Header Summary Section -->
    <v-row class="mb-8 px-2 align-center">
      <div class="d-flex align-center">
        <v-avatar color="success lighten-5" size="48" class="mr-4">
          <v-icon color="success">fas fa-hand-holding-usd</v-icon>
        </v-avatar>
        <div>
          <div class="text-h5 font-weight-black primary--text lh-1">Pagos Recibidos</div>
          <div class="caption grey--text font-weight-bold uppercase tracking-widest mt-1">Saldos y abonos para Factura #{{invoice.document_id}}</div>
        </div>
      </div>
      <v-spacer></v-spacer>
      <v-btn 
        color="success" 
        class="font-weight-black text-uppercase tracking-widest rounded-lg px-6 h-44 shadow-sm"
        @click="()=>{ toPayment=true; newPayment.amount=invoice.get_total-invoice.get_payed }" 
        :disabled="invoice.estatus>2"
      >
        <v-icon left small>fas fa-plus</v-icon> Registrar Pago
      </v-btn>
    </v-row>

    <v-row>
      <!-- Financial Summary Sidebar -->
      <v-col cols="12" md="4">
        <v-card outlined class="modern-card pa-6 bg-lightgrey mb-6">
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-4">Resumen de Cuenta</div>
          
          <div class="d-flex flex-column gap-3">
            <div class="info-row">
              <span class="caption grey--text font-weight-bold">Total Documento</span>
              <span class="body-2 font-weight-black primary--text">{{invoice.get_total| moneda}}</span>
            </div>
            <div class="info-row">
              <span class="caption success--text font-weight-black">Total Pagado</span>
              <span class="body-2 font-weight-black success--text">{{invoice.get_payed| moneda}}</span>
            </div>
            
            <v-divider class="my-4"></v-divider>

            <div class="info-row pa-4 rounded-xl" :class="(invoice.get_total-invoice.get_payed) > 0 ? 'error lighten-5 border-error' : 'success lighten-5 border-success'">
              <div class="d-flex flex-column">
                <span class="caption font-weight-black" :class="(invoice.get_total-invoice.get_payed) > 0 ? 'error--text' : 'success--text'">SALDO PENDIENTE</span>
                <span class="text-h4 font-weight-black tracking-tighter mt-1" :class="(invoice.get_total-invoice.get_payed) > 0 ? 'error--text' : 'success--text'">
                  {{invoice.get_total-invoice.get_payed| moneda}}
                </span>
              </div>
            </div>
          </div>

          <v-divider class="my-6"></v-divider>
          
          <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-2">Cliente</div>
          <div class="caption font-weight-black primary--text lh-1-2">{{invoice.rznSocRecep}}</div>
          <div class="caption grey--text font-weight-bold">{{invoice.rutRecep}}</div>
        </v-card>
      </v-col>

      <!-- Payments History Table -->
      <v-col cols="12" md="8">
        <v-card outlined class="modern-card overflow-hidden">
          <div class="px-6 py-4 border-bottom bg-lightgrey d-flex align-center">
            <span class="caption font-weight-black uppercase tracking-widest grey--text">Historial de Abonos</span>
            <v-spacer></v-spacer>
            <v-chip x-small color="primary lighten-5" class="primary--text font-weight-black">{{(invoice.payments || []).length}} registros</v-chip>
          </div>

          <v-data-table
            :headers="headers"
            :items="invoice.payments || []"
            :items-per-page="10"
            :loading="loading"
            class="modern-table"
            sort-by='date'
            :sort-desc="true"
          >
            <template v-slot:no-data>
              <div class="pa-12 text-center grey--text opacity-60">
                <v-icon size="48" class="mb-4">fas fa-receipt</v-icon>
                <p class="body-2 font-weight-bold">No se han registrado pagos aún</p>
              </div>
            </template>

            <template v-slot:item.tipo="{ item }">
              <v-chip x-small label class="font-weight-black" color="grey lighten-4">
                {{tipos[item.tipo-1]}}
              </v-chip>
            </template>

            <template v-slot:item.date="{ item }">
              <span class="body-2 font-weight-medium grey--text text--darken-3">{{item.date | moment('DD/MM/YYYY')}}</span>
            </template>

            <template v-slot:item.glosa="{ item }">
              <span class="caption grey--text text--darken-2 font-weight-medium">{{item.glosa}}</span>
            </template>

            <template v-slot:item.amount="{ item }">
              <span class="body-2 font-weight-black success--text">{{item.amount| moneda}}</span>
            </template>

            <template v-slot:item.actions="{ item }">
              <v-btn icon x-small color="error" class="grey lighten-4" @click="paymentToRemove=item; showConfirmDelete=true">
                <v-icon x-small>fas fa-trash</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-col>
    </v-row>

    <!-- Register Payment Dialog -->
    <v-dialog v-model="toPayment" max-width="500" persistent overlay-color="primary" overlay-opacity="0.2">
      <v-card class="modern-card overflow-hidden">
        <div class="success pa-6 d-flex align-center">
          <v-avatar color="white" size="32" class="mr-4">
            <v-icon color="success" size="16">fas fa-plus</v-icon>
          </v-avatar>
          <span class="text-h6 font-weight-black white--text">Registrar Abono</span>
          <v-spacer></v-spacer>
          <v-btn icon @click="toPayment=false" dark small>
            <v-icon small>fas fa-times</v-icon>
          </v-btn>
        </div>

        <div class="pa-8">
          <ValidationObserver v-slot="{handleSubmit}">
            <v-form @submit.prevent="handleSubmit(addPayment)">
              <v-row dense>
                <v-col cols="12">
                  <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Fecha de Pago</div>
                  <ValidationProvider name="Fecha" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model="newPayment.date"
                      type="date"
                      outlined dense
                      class="modern-input"
                      hide-details="auto"
                      :error-messages="errors"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" class="mt-4">
                  <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Medio de Pago</div>
                  <ValidationProvider name="Tipo" rules="required" v-slot="{ errors }">
                    <v-select
                      v-model="newPayment.tipo"
                      :items="tipos"
                      placeholder="Seleccione método"
                      outlined dense
                      class="modern-input"
                      hide-details="auto"
                      :error-messages="errors"
                    />
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" class="mt-4">
                  <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Monto del Abono</div>
                  <ValidationProvider name="Monto" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model="newPayment.amount"
                      type="number"
                      prefix="$"
                      outlined dense
                      class="modern-input"
                      hide-details="auto"
                      :error-messages="errors"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>

                <v-col cols="12" class="mt-4">
                  <div class="caption grey--text font-weight-black text-uppercase tracking-widest mb-1 ml-1">Glosa / Detalle</div>
                  <ValidationProvider name="Detalle" rules="required" v-slot="{ errors }">
                    <v-text-field
                      v-model="newPayment.glosa"
                      placeholder="Ej: Transferencia Banco Estado"
                      outlined dense
                      class="modern-input"
                      hide-details="auto"
                      :error-messages="errors"
                    ></v-text-field>
                  </ValidationProvider>
                </v-col>
              </v-row>

              <div class="d-flex mt-8 gap-3">
                <v-btn block color="success" type="submit" :loading="loadingAddPayment" class="font-weight-black text-uppercase tracking-widest h-44 rounded-lg">
                  Confirmar Pago
                </v-btn>
              </div>
            </v-form>
          </ValidationObserver>
        </div>
      </v-card>
    </v-dialog>

    <confirm
      v-model="showConfirmDelete" 
      message="¿Está seguro de eliminar el abono seleccionado?"
      @ok="removePayment"
      @cancel="showConfirmDelete=false"
    />
  </div>
</template>

<script>
import { createPayment, deletePayment } from '@/api/salesNotes'
import notifications from '@/utils/notifications'

export default {
  name: 'InvoicePayDetail',
  props: ['invoice'],
  data() {
    return {
      tipos: ['EFECTIVO', 'CHEQUE', 'TRANSFERENCIA', 'DEPOSITO'],
      headers: [
        { text: 'Método', align: 'start', value: 'tipo', class: 'table-header', width: '130px' },
        { text: 'Fecha', align: 'start', value: 'date', class: 'table-header', width: '120px' },
        { text: 'Glosa / Referencia', align: 'start', value: 'glosa', class: 'table-header' },
        { text: 'Monto', align: 'end', value: 'amount', class: 'table-header', width: '130px' },
        { text: '', align: 'end', value: 'actions', class: 'table-header', sortable: false, width: '60px' },
      ],
      newPayment: {
        date: new Date().toISOString().substr(0, 10),
        tipo: '',
        glosa: '',
        amount: 0
      },
      toPayment: false,
      loading: false,
      loadingAddPayment: false,
      paymentToRemove: '',
      showConfirmDelete: false,
    }
  },
  methods: {
    async addPayment() {
      this.loadingAddPayment = true
      try {
        const data = await createPayment({
          date: this.newPayment.date,
          amount: this.newPayment.amount,
          tipo: this.tipos.indexOf(this.newPayment.tipo) + 1,
          glosa: this.newPayment.glosa,
          dte: this.invoice.id,
        })
        this.toPayment = false
        this.invoice.payments.push(data)
        this.newPayment = { date: new Date().toISOString().substr(0, 10), tipo: '', glosa: '', amount: 0 }
        notifications.showSuccess('Pago registrado con éxito')
        this.$emit('refresh')
      } catch (err) {
        notifications.showError(err.response?.data?.message || 'Error al registrar el pago')
      } finally {
        this.loadingAddPayment = false
      }
    },
    async removePayment() {
      this.loading = true
      try {
        await deletePayment(this.paymentToRemove.id)
        notifications.showSuccess('Pago eliminado con éxito')
        this.$emit('refresh')
      } catch (err) {
        notifications.showError(err.response?.data?.message || 'Error al eliminar el pago')
      } finally {
        this.loading = false
        this.paymentToRemove = ''
        this.showConfirmDelete = false
      }
    }
  }
}
</script>

<style scoped>
.modern-card { border-radius: 12px !important; border: 1px solid rgba(0,0,0,0.05) !important; background-color: white !important; }
.bg-lightgrey { background-color: #f8fbfc; }
.border-bottom { border-bottom: 1px solid rgba(0,0,0,0.05) !important; }
.border-error { border: 2px solid #fee2e2 !important; }
.border-success { border: 2px solid #d1fae5 !important; }
.lh-1 { line-height: 1 !important; }
.lh-1-2 { line-height: 1.2 !important; }
.gap-3 { gap: 12px; }
.uppercase { text-transform: uppercase; }
.tracking-widest { letter-spacing: 1.8px; }
.tracking-tighter { letter-spacing: -1px; }
.h-44 { height: 44px !important; }
.opacity-60 { opacity: 0.6; }

.info-row { display: flex; justify-content: space-between; align-items: flex-start; }

.modern-input >>> fieldset { border-radius: 8px !important; border-color: #e0e4e8 !important; }

.modern-table >>> thead th { background-color: #f8fbfc !important; height: 52px !important; text-transform: uppercase; font-size: 0.75rem !important; letter-spacing: 1px; font-weight: 900 !important; color: #607d8b !important; }
.modern-table >>> td { height: 64px !important; border-bottom: 1px solid #f0f3f5 !important; }
</style>